import angular from 'angular';
import stubFalse from 'lodash.stubfalse';

const TAB = '\t';
const ENTER = '\n';
const TAB_KEYCODE = 9;
const ENTER_KEYCODE = 13;
const DELETE_KEYCODE = 8;
const DEFAULT_DELIMITERS = [',', TAB, ENTER];
const PLACEHOLDER_KEY = 'components.tag-input.placeholder';
const NEW_TAG_LABEL_KEY = 'components.tag-input.taggingLabel';
const NO_FILTER_RESULTS_KEY = 'components.tag-input.noFilterResults';
const ITEM_FAILURE_MESSAGE_KEY = 'components.tag-input.itemFailureMessage';

export default class TagInputController {

  static get $inject() {
    return ['$scope', '$log', '$attrs', '$element', '$parse', '$translate', 'appendToBodyService',
      '$timeout', '$filter', '$window', '$animate', '$q'];
  }

  static isEmpty(value) {
    return angular.isArray(value) ? value.length === 0 : true;
  }

  constructor($scope, $log, $attrs, $element, $parse, $translate, appendToBodyService, $timeout,
              $filter, $window, $animate, $q) {
    this.$scope = $scope;
    this.$log = $log;
    this.$translate = $translate;
    this.appendToBodyService = appendToBodyService;
    this.$element = $element;
    this.$parse = $parse;
    this.$timeout = $timeout;
    this.$filter = $filter;
    this.$attrs = $attrs;
    this.$window = $window;
    this.$animate = $animate;
    this.$q = $q;
  }

  $onInit() {
    this.isDraggable = angular.isDefined(this.$attrs.isDraggable);
    this.appendedToBody = angular.isDefined(this.$attrs.appendedToBody);
    this.stacked = angular.isDefined(this.$attrs.stacked);
    this.caseSensitive = angular.isDefined(this.$attrs.caseSensitive);

    this.proposedTag = '';
    this.canDelete = false;
    this.selectedTags = [];
    this.selectedTagi18nMap = new Map();
    this.menuTags = [];
    this.menuTagMap = new Map();
    this.pristineMenuTagI18nMap = new Map();

    this.tagInputPromiseLoading = false;
    this.scrollBarClicked = false;

    this.getTextProperty = this.$parse(this.textProperty);
    this.setTextProperty = this.getTextProperty.assign;

    this.sortableConfig = {
      draggable: '.tag',
      filter: '.tag-input-container',
      disabled: !this.isDraggable || !this.canEdit(),
      animation: 150,
      onSort: () => {
        // this fixes an issue with tag-input-container being
        // moved away from the last position
        const inputElem = angular.element(this.tagListElem[0]
          .querySelector('.tag-input-container'))
          .detach();
        this.tagListElem.append(inputElem);
        this.updateModel();
      }
    };

    this.initDelimiterSets();
    this.initTranslations();

    this.$attrs.$observe('restricted', restricted => {
      this.restricted = angular.isDefined(restricted);
    });
    this.$scope.$watchCollection('taginput.items', this.initMenuTags.bind(this));
    this.$scope.$watch('taginput.proposedTag', this.inspectProposedTag.bind(this));
  }

  initialize(elem, ngModel) {
    this.ngModel = ngModel;

    this.$animate.enabled(elem, false);

    this.tagMenu = angular.element(this.$element[0].querySelector('.dropdown-menu'));
    this.tagListElem = angular.element(this.$element[0].querySelector('.tag-input-list'));

    // to minimize watches
    this.tagMenu.toggleClass('tagInputAppendedMenu', this.appendedToBody);
    this.tagListElem.toggleClass('draggable', this.isDraggable);
    this.tagListElem.toggleClass('stacked', this.stacked);

    this.tagInput = this.$element[0].querySelector('.tag-input');

    ngModel.$formatters.push(modelValue => {
      if (!angular.isArray(modelValue)) {
        return modelValue;
      }

      const viewValue = [];

      // clear the Map container for reset
      this.selectedTagi18nMap.clear();

      modelValue.forEach(modelItem => {

        // Use instant because async seems breaking the pipeline flow,
        // the viewValue won't be correct values when return, then
        // render called with incorrect values set to selectedTags
        this.selectedTags = angular.isArray(this.ngModel.$viewValue) ?
          this.ngModel.$viewValue : [];

        this.syncMenuTagsMap();

        const response = translatedModelItem => {
          const viewValueItem = this.textProperty ? angular.copy(modelItem) : translatedModelItem;
          if (this.textProperty) {
            this.setTextProperty(viewValueItem, translatedModelItem);
          }
          viewValue.push(viewValueItem);
          const translatedModelItemKey =
            this.caseSensitive ? translatedModelItem : translatedModelItem.toLowerCase();
          this.selectedTagi18nMap.set(translatedModelItemKey, modelItem);
          this.selectedTags = angular.isArray(viewValue) ? viewValue : [];
          this.syncMenuTagsMap();
        };

        /**
         * https://track.akamai.com/jira/browse/UIE-110
         *
         * Please note that this formatter always returns empty viewValue (viewValue is populated
         * asynchronously in the above response callback after translation process),
         * even if modelValue is valid non-empty array. This causes that required validation
         * is not passed in the next synchronous phases.
         *
         * We try to fix it by replace $isEmpty validator by stub false func (we can do it
         * because we are sure that modelValue is non-empty array here) until model translation
         * phase is finished. Then standard validator is restored to be able to validate model
         * properly in further user actions.
         */
        this.ngModel.$isEmpty = stubFalse;
        this.$translate(this.caseSensitiveTag(modelItem))
          .then(translatedModelItem => response(translatedModelItem))
          .catch(translatedModelItem => response(translatedModelItem))
          .finally(() => {
            this.ngModel.$isEmpty = TagInputController.isEmpty;
          });
      });

      return viewValue;
    });

    ngModel.$render = () => {
      this.selectedTags = angular.isArray(this.ngModel.$viewValue) ?
        this.ngModel.$viewValue : [];
    };

    if (this.onValidate) {
      ngModel.$validators.validTags = tags => {
        const validationMap = this.getValidationMap(tags);
        this.setValidationClasses(validationMap);
        return validationMap ?
          Array.from(validationMap.values()).every(validationResult => validationResult) : false;
      };
    }

    ngModel.$isEmpty = TagInputController.isEmpty;

    ngModel.$parsers.push(viewValue => {
      if (!angular.isArray(viewValue)) {
        return viewValue;
      }
      const modelValue = [];
      viewValue.forEach(viewValueItem => {
        const selectedTag = this.selectedTagi18nMap.get(this.tag(viewValueItem)) ||
          this.pristineMenuTagI18nMap.get(this.tag(viewValueItem));

        modelValue.push(selectedTag || viewValueItem);
      });
      return modelValue;
    });

    this.appendToBody();
    this.setupSelectedTags();
    this.setAutoFocus();
  }

  initMenuTags(items) {
    const addItemsToMenuTagMap = tagItems => {
      if (!angular.isArray(tagItems)) {
        throw new Error('Tag items must be an Array');
      }

      if (this.canSetTheMenuTags(tagItems)) {
        this.menuTags = [];
        this.menuTagMap.clear();
        this.pristineMenuTagI18nMap.clear();
        const response = (translatedTag, tagItem) => {
          let newTagItem;
          if (this.textProperty) {
            newTagItem = angular.copy(tagItem);
            this.setTextProperty(newTagItem, translatedTag);
          } else {
            newTagItem = translatedTag;
          }
          if (!this.isTagInSelectedList(newTagItem)) {
            this.menuTags.push(newTagItem);
            this.menuTagMap.set(this.tag(newTagItem), newTagItem);
          }
          this.pristineMenuTagI18nMap.set(this.tag(newTagItem), tagItem);
        };

        tagItems.forEach((tagItem) => {
          this.$translate(this.caseSensitiveTag(tagItem))
            .then(translatedTag => response(translatedTag, tagItem))
            .catch(translatedTag => response(translatedTag, tagItem));
        });
      } else {
        throw new Error('The menu tags must contain the selected tag (In restricted mode)');
      }
    };

    items = items || [];

    this.tagInputPromiseLoading = true;
    this.tagInputPromiseError = false;

    this.$q.when(items)
      .then(promiseItems => {
        promiseItems = promiseItems && promiseItems.hasOwnProperty('data') ?
          promiseItems.data : promiseItems;
        addItemsToMenuTagMap(promiseItems);
      })
      .catch(() => {
        this.tagInputPromiseError = true;
      })
      .finally(() => {
        this.tagInputPromiseLoading = false;
      });
  }

  canSetTheMenuTags(tagItems) {
    const intersection = this.selectedTags.filter(selectedTag =>
        tagItems.some( menuTag => this.tag(selectedTag) === this.tag(menuTag)));

    return !this.restricted ? true : intersection.length === this.selectedTags.length;
  }

  initDelimiterSets() {
    this.delimiterSet = new Set();
    this.submitKeyCodeSet = new Set();

    this.delimiters = this.delimiters || DEFAULT_DELIMITERS;

    this.delimiters.forEach(delimiter => {
      switch (true) {
        case delimiter === TAB || delimiter === 'TAB':
          this.submitKeyCodeSet.add(TAB_KEYCODE);
          this.delimiterSet.add(TAB);
          break;
        case delimiter === ENTER || delimiter === 'ENTER':
          this.submitKeyCodeSet.add(ENTER_KEYCODE);
          this.delimiterSet.add(ENTER);
          break;
        default:
          this.delimiterSet.add(delimiter);
      }
    });
  }

  initTranslations() {
    this.$translate(this.placeholder || PLACEHOLDER_KEY)
      .then(value => this.translatedPlaceholder = value)
      .catch(value => this.translatedPlaceholder = value);

    this.$translate(this.newTagLabel || NEW_TAG_LABEL_KEY)
      .then(value => this.translatedNewTagLabel = value)
      .catch(value => this.translatedNewTagLabel = value);

    this.$translate(this.noFilterResults || NO_FILTER_RESULTS_KEY)
      .then(value => this.translatedNoFilterResults = value)
      .catch(value => this.translatedNoFilterResults = value);

    this.$translate(this.itemFailureMessage || ITEM_FAILURE_MESSAGE_KEY)
      .then(value => this.translatedItemFailureMessage = value)
      .catch(value => this.translatedItemFailureMessage = value);
  }

  setupSelectedTags() {
    this.$timeout(() => {
      this.sortSelectedTags().then(() => this.setSelectedTagsClasses());
    });
  }

  setSelectedTagsClasses() {
    // We need to wrap this in a $timeout because the tag elements are in the middle of
    // rendering in this $digest cycle and won't be available until the next cycle.
    this.$timeout(() => {
      const selectedTagElems = this.$element[0].querySelectorAll('li.tag');

      for (let i = 0, len = selectedTagElems.length; i < len; i++) {
        const selectedTagLi = angular.element(selectedTagElems[i]);
        selectedTagLi.toggleClass('lastTag', i === this.selectedTags.length - 1);
      }
    });
  }

  setValidationClasses(validationMap) {
    this.$timeout(() => {
      const selectedTagElems = this.$element[0].querySelectorAll('li.tag');

      for (let i = 0, len = selectedTagElems.length; i < len; i++) {
        const selectedTagLi = angular.element(selectedTagElems[i]);
        if (validationMap) {
          selectedTagLi.toggleClass('invalid', !validationMap.get(this.tag(this.selectedTags[i])));
        }
      }
    });
  }

  setAutoFocus() {
    if (angular.isDefined(this.$attrs.autofocus) &&
      !(this.isDisabled || this.isReadonly)) {
      this.$timeout(() => this.tagInput.focus());
      this.focus();
    }
  }

  canEdit() {
    return !this.isDisabled && !this.isReadOnly;
  }

  // Only use this method to get the key for a map. Do not use this for rendering
  tag(tag) {
    return this.caseSensitive ? this.caseSensitiveTag(tag) :
      this.caseSensitiveTag(tag).toLowerCase();
  }

  caseSensitiveTag(tag) {
    return angular.isString(tag) ? tag : this.getTextProperty(tag);
  }

  getFilteredMenuTags() {
    return this.tagInputPromiseLoading ? [] :
      this.$filter('filter')(this.menuTags, this.proposedTag);
  }

  highlightMatchingMenuTags() {
    // wrap in $timeout so that menu tag elements finish rendering
    this.$timeout(() => {
      const menuTagElems = this.$element[0].querySelectorAll('li.menu-tag > a > span');

      for (let i = 0, len = menuTagElems.length; i < len; i++) {
        const menuTagLi = angular.element(menuTagElems[i]);
        const tag = menuTagLi.text().trim();
        const menuTagHtml = this.proposedTag.length ?
          this.$filter('highlight')(tag, this.proposedTag) : tag;

        menuTagLi.html(menuTagHtml);
      }
    });
  }

  appendToBody() {
    if (this.appendedToBody) {
      this.appendToBodyService.appendToBody(this.$element, this.tagMenu, () => {
        this.tagMenu.css('width', `${this.$element[0].getBoundingClientRect().width}px`);
      });
    }
  }

  inspectProposedTag(proposedTag) {
    if (this.appendedToBody) {
      this.tagMenu.toggleClass('util-show', proposedTag);
      this.tagMenu.toggleClass('util-hide', !proposedTag);
    }

    this.highlightMatchingMenuTags();

    if (!proposedTag.length) {
      return;
    }

    if (proposedTag.length === 1 &&
      this.delimiterSet.has(proposedTag)) {
      this.proposedTag = '';
      return;
    }

    if ([...this.delimiterSet].some(delimiter => proposedTag.includes(delimiter)) &&
      proposedTag.length > 1) {
      this.parseTagsString(proposedTag).forEach(tag => this.addTag(tag));
      this.proposedTag = '';
    }
  }

  assessKeyDown(event) {

    if (event.which === DELETE_KEYCODE && !this.proposedTag) {
      const lastTag = this.getLastSelectedTag();
      if (angular.isUndefined(lastTag)) {
        return;
      }
      if (this.canDelete) {
        this.removeTag(lastTag);
        this.updateModel();
        this.canDelete = false;
      } else {
        this.canDelete = true;
      }
      return;
    }
    this.canDelete = false;
    if (this.submitKeyCodeSet.has(event.which)) {
      event.preventDefault();

      this.addTag(this.proposedTag);
      this.proposedTag = '';
    }
  }

  addTag(tag) {
    if (angular.isString(tag)) {
      tag = tag.trim();
    }
    if (this.canAddTag(tag)) {
      this.selectedTags.push(this.getNewSelectedTag(tag));
      this.menuTagMap.delete(this.tag(tag));
      this.syncMenuTags();
      this.updateModel();
    }
  }

  removeTag(tag) {
    this.selectedTags.splice(this.selectedTags.indexOf(tag), 1);
    //not to add if item is not in the original list
    if (this.pristineMenuTagI18nMap.has(this.tag(tag))) {
      this.menuTagMap.set(this.tag(tag), tag);
      this.syncMenuTags();
    }
    this.updateModel();
  }

  syncMenuTags() {
    this.menuTags = [...this.menuTagMap.values()];
    this.menuTags.sort();

    if (this.menuTags.length === 0) {
      return;
    }
    if (angular.isString(this.menuTags[0])) {
      this.menuTags.sort();
    } else {
      this.menuTags.sort((a, b) => {
        const aTag = this.tag(a);
        const bTag = this.tag(b);
        if (aTag < bTag) {
          return -1;
        }
        if (aTag > bTag) {
          return 1;
        }
        return 0;
      });
    }
  }

  syncMenuTagsMap() {
    //reload the items
    this.menuTagMap.clear();
    for (const tag of this.pristineMenuTagI18nMap.values()) {
      if (!this.isTagInSelectedList(tag)) {
        this.menuTagMap.set(this.tag(tag), tag);
      }
    }
    this.syncMenuTags();
  }

  sortSelectedTags() {
    return this.$timeout(() => {
      if (angular.isFunction(this.onSort)) {
        this.onSort({tags: this.selectedTags});
      }
    });
  }

  canAddTag(tag) {
    if (tag && !this.selectedTags.find(selectedTag => this.tag(selectedTag) === this.tag(tag))) {
      return !(this.restricted && !this.isTagInList(tag));
    }
  }

  getLastSelectedTag() {
    return this.selectedTags.length ?
      this.selectedTags[this.selectedTags.length - 1] : undefined;
  }

  tagValid(tag) {
    return angular.isFunction(this.onValidate) ? this.onValidate({tag}) : true;
  }

  getNewSelectedTag(tag) {
    if (!this.isTagInList(tag)) {
      if (this.textProperty) {
        const newTag = {};
        this.setTextProperty(newTag, tag);
        return newTag;
      } else {
        return tag;
      }
    }
    return this.menuTagMap.get(this.tag(tag));
  }

  isTagInList(tag) {
    return this.menuTagMap.has(this.tag(tag));
  }

  isTagInSelectedList(tag) {
    return this.selectedTagi18nMap.has(this.tag(tag));
  }

  parseTagsString(tagsString) {
    const tagSet = new Set();
    let hasDelimiter = false;

    // iterate over each delimiter
    this.delimiterSet.forEach(delimiter => {

      // if the tagString includes this delimiter, replace the delimiter with a new line
      // character which will be used as a marker for the final split.
      if (tagsString.includes(delimiter)) {
        hasDelimiter = true;
        tagsString = tagsString.split(delimiter).join('\n');
      }
    });

    // if it had a delimiter do a final split and return an array of unique tags
    if (hasDelimiter) {
      tagsString.split('\n').forEach(tag => {
        if (tag) {
          tagSet.add(tag);
        }
      });
      return Array.from(tagSet);
    }
    return [tagsString];
  }

  menuMouseDown(e) {
    if ((/msie|trident/i).test(this.$window.navigator.userAgent)) {
      this.scrollBarClicked = true;
    }

    e.preventDefault();
  }

  setSelectedTag(tag, e) {
    // stop propagation on anchor behavior
    e.stopPropagation();

    this.scrollBarClicked = false;

    if (this.restricted) {
      if (this.isTagInList(tag)) {
        this.addTag(tag);
      }
    } else {
      this.addTag(tag);
    }
    this.proposedTag = '';
  }

  blur() {
    if (this.proposedTag) {
      this.addTag(this.proposedTag);
    }

    if (this.scrollBarClicked) {
      this.scrollBarClicked = false;
      this.$timeout(() => this.tagInput.focus());
      return;
    }
    this.focused = false;
    this.proposedTag = '';
    this.tagMenu.addClass('util-hide');
    this.tagMenu.addClass('util-invisible');
    this.tagMenu.removeClass('util-show');
    this.tagMenu.removeClass('util-visible');
    this.ngModel.$setTouched();

    if (angular.isFunction(this.onBlur)) {
      this.onBlur({});
    }
  }

  focus() {
    if (this.isDisabled || this.isReadonly) {
      return;
    }
    this.appendToBody();
    this.focused = true;
    this.tagMenu.addClass('util-show');
    this.tagMenu.addClass('util-visible');
    this.tagMenu.removeClass('util-hide');
    this.tagMenu.removeClass('util-invisible');
  }

  updateModel() {
    if (angular.isFunction(this.onSort)) {
      this.onSort({tags: this.selectedTags});
    }
    this.ngModel.$setViewValue(angular.copy(this.selectedTags));
    this.ngModel.$setTouched();
    this.setSelectedTagsClasses();
    this.appendToBody();
  }

  getValidationMap(tags) {
    let validationMap;
    if (angular.isFunction(this.onValidate) && angular.isArray(tags)) {
      validationMap = new Map();
      tags.forEach(tag => validationMap.set(this.tag(tag), this.tagValid(tag)));
    }
    return validationMap;
  }
}
